import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Button, Input, Select, Label, Icon } from '../../components/ui';

const DEFAULT_ACCOUNTS = [
  { value: '411100', label: '411100 - Clients' },
  { value: '401000', label: '401000 - Fournisseurs' },
  { value: '701000', label: '701000 - Ventes' },
  { value: '705000', label: '705000 - Prestations' },
  { value: '601000', label: '601000 - Achats' },
  { value: '445710', label: '445710 - TVA collectée 19%' },
  { value: '445660', label: '445660 - TVA déductible 19%' },
  { value: '635000', label: '635000 - Droits de timbre' },
  { value: '641100', label: '641100 - Rémunérations' },
  { value: '613000', label: '613000 - Loyers' },
  { value: '606000', label: '606000 - Charges diverses' },
  { value: '164000', label: '164000 - Avances reçues' },
  { value: '628100', label: '628100 - Charges diverses' },
  { value: '658000', label: '658000 - Autres charges' },
];

export default function AccountingSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/accounting/settings');
      setSettings(res.data?.settings || null);
    } catch {
      toast.error('Erreur chargement paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/accounting/settings', {
        defaults: settings.defaults,
        journals: settings.journals,
        treasuryCategoryMapping: settings.treasuryCategoryMapping,
      });
      toast.success('Paramètres enregistrés');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateDefault = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      defaults: { ...prev.defaults, [key]: value },
    }));
  };

  const updateJournal = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      journals: { ...prev.journals, [key]: value },
    }));
  };

  const updateTreasuryMapping = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      treasuryCategoryMapping: { ...prev.treasuryCategoryMapping, [key]: value },
    }));
  };

  if (loading) return <div className="min-h-screen bg-gray-50 p-6"><div>Chargement...</div></div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconSettings" className="text-indigo-600 mr-3" />
          Paramètres comptables
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-lg font-bold text-gray-800 mb-4">Comptes par défaut</h2>
          <div className="space-y-3">
            <div>
              <Label>Compte Clients (AR)</Label>
              <Select value={settings?.defaults?.arAccount || ''} onChange={(e) => updateDefault('arAccount', e.target.value)}>
                {DEFAULT_ACCOUNTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Compte Fournisseurs (AP)</Label>
              <Select value={settings?.defaults?.apAccount || ''} onChange={(e) => updateDefault('apAccount', e.target.value)}>
                {DEFAULT_ACCOUNTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Compte Ventes</Label>
              <Select value={settings?.defaults?.salesAccount || ''} onChange={(e) => updateDefault('salesAccount', e.target.value)}>
                {DEFAULT_ACCOUNTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Compte Achats</Label>
              <Select value={settings?.defaults?.purchasesAccount || ''} onChange={(e) => updateDefault('purchasesAccount', e.target.value)}>
                {DEFAULT_ACCOUNTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>TVA Collectée</Label>
              <Select value={settings?.defaults?.vatCollectedAccount || ''} onChange={(e) => updateDefault('vatCollectedAccount', e.target.value)}>
                {DEFAULT_ACCOUNTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>TVA Déductible</Label>
              <Select value={settings?.defaults?.vatDeductibleAccount || ''} onChange={(e) => updateDefault('vatDeductibleAccount', e.target.value)}>
                {DEFAULT_ACCOUNTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Compte Timbre</Label>
              <Select value={settings?.defaults?.stampDutyAccount || ''} onChange={(e) => updateDefault('stampDutyAccount', e.target.value)}>
                {DEFAULT_ACCOUNTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </Select>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-lg font-bold text-gray-800 mb-4">Journaux</h2>
          <div className="space-y-3">
            <div>
              <Label>Journal Ventes</Label>
              <Input value={settings?.journals?.sales || 'VE'} onChange={(e) => updateJournal('sales', e.target.value)} />
            </div>
            <div>
              <Label>Journal Achats</Label>
              <Input value={settings?.journals?.purchases || 'AC'} onChange={(e) => updateJournal('purchases', e.target.value)} />
            </div>
            <div>
              <Label>Journal Banque</Label>
              <Input value={settings?.journals?.bank || 'BQ'} onChange={(e) => updateJournal('bank', e.target.value)} />
            </div>
            <div>
              <Label>Journal Caisse</Label>
              <Input value={settings?.journals?.cash || 'CA'} onChange={(e) => updateJournal('cash', e.target.value)} />
            </div>
            <div>
              <Label>Journal OD</Label>
              <Input value={settings?.journals?.od || 'OD'} onChange={(e) => updateJournal('od', e.target.value)} />
            </div>
          </div>

          <h2 className="text-lg font-bold text-gray-800 mt-6 mb-4">Catégories Trésorerie</h2>
          <div className="space-y-3">
            {['salary', 'rent', 'utilities', 'tax', 'loan', 'other'].map((cat) => (
              <div key={cat}>
                <Label className="capitalize">{cat}</Label>
                <Input value={settings?.treasuryCategoryMapping?.[cat] || ''} onChange={(e) => updateTreasuryMapping(cat, e.target.value)} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
        </Button>
      </div>
    </div>
  );
}
